/* CL Body Map — interactive hotspot state machine (Cellova Health)
   Ported from Bettr custom-pdp-hotspots.js. Changes vs source:
     • custom element renamed bettr-hotspot-modal → cl-bodymap-modal
     • all selectors data-cl-bodymap-*
     • BettrMetrics hook removed
     • pill-tab row (Fatty15) added — a pill opens the same hotspot as its dot
     • reduced-motion respected (skips zoom transform on the modal image)
   Kept from source: cover-aware dot re-projection through object-position, one
   <dialog> whose content pane swaps, prev/next + arrow-key cycle + counter,
   setTimeout(0)-after-showModal trap, Esc fade, focus restore, section:unload
   teardown, Theme-Editor section:load re-init. */

(function () {
  'use strict';

  if (typeof customElements !== 'undefined' && !customElements.get('cl-bodymap-modal')) {
    customElements.define('cl-bodymap-modal', class extends HTMLElement {});
  }

  var ZOOM = 2.4;        // zoom factor for the mobile modal image
  var TRANSITION = 220;  // ms — matches CSS opacity transition on the dialog

  var REDUCED = false;
  try {
    REDUCED = window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  } catch (e) { REDUCED = false; }

  function initSection(section) {
    if (!section || section.dataset.clBodymapInit === '1') return;
    section.dataset.clBodymapInit = '1';

    var sectionId = section.dataset.clBodymapId || '';
    var dots = Array.prototype.slice.call(section.querySelectorAll('[data-cl-bodymap-dot]'));
    var pills = Array.prototype.slice.call(section.querySelectorAll('[data-cl-bodymap-pill]'));

    // Resolve the modal by id (sibling of <section>, rendered inside the
    // {% section %} wrapper) rather than by DOM traversal.
    var modal = document.getElementById('clbm-modal-' + sectionId.replace(/_/g, '-'));
    if (!modal) {
      var parent = section.parentNode;
      modal = parent ? parent.querySelector('[data-cl-bodymap-modal]') : null;
    }
    if (!modal) return;

    // Count of interactive content panes. If there are no dots (no image) the
    // pills still work as tab openers — so we only bail when BOTH are empty.
    if (dots.length === 0 && pills.length === 0) return;

    var modalImage = modal.querySelector('[data-cl-bodymap-modal-image]');
    var modalClose = modal.querySelector('[data-cl-bodymap-close]');
    var modalOverlay = modal.querySelector('[data-cl-bodymap-overlay]');
    var modalPrev = modal.querySelector('[data-cl-bodymap-prev]');
    var modalNext = modal.querySelector('[data-cl-bodymap-next]');
    var modalCounter = modal.querySelector('[data-cl-bodymap-counter]');
    var contentStack = modal.querySelectorAll('[data-cl-bodymap-content]');

    var total = contentStack.length;
    if (total === 0) return;

    var canvasImage = section.querySelector('[data-cl-bodymap-image]');
    var canvasEl = section.querySelector('[data-cl-bodymap-canvas]');

    var currentIndex = -1;
    var lastFocused = null;
    var teardown = [];

    function on(target, event, handler, opts) {
      target.addEventListener(event, handler, opts);
      teardown.push(function () { target.removeEventListener(event, handler, opts); });
    }

    // Cover-aware dot positioning. data-cl-dot-x/y are % of the SOURCE image; JS
    // re-projects them into canvas % accounting for object-fit: cover + the CSS
    // object-position, so a dot stays glued to the same image feature at any
    // breakpoint / aspect (Bettr port).
    function positionDots() {
      if (!canvasImage || !canvasEl || dots.length === 0) return;
      var iw = canvasImage.naturalWidth;
      var ih = canvasImage.naturalHeight;
      if (!iw || !ih) return;
      var cRect = canvasEl.getBoundingClientRect();
      var cw = cRect.width, ch = cRect.height;
      if (!cw || !ch) return;
      var op = (getComputedStyle(canvasImage).objectPosition || '50% 50%').trim();
      var parts = op.split(/\s+/);
      function parseOp(token, fallback) {
        if (!token) return fallback;
        if (token === 'center') return 0.5;
        if (token === 'left' || token === 'top') return 0;
        if (token === 'right' || token === 'bottom') return 1;
        var m = token.match(/^(-?\d+(?:\.\d+)?)%$/);
        if (m) return parseFloat(m[1]) / 100;
        return fallback;
      }
      var opX = parseOp(parts[0], 0.5);
      var opY = parseOp(parts[1], 0.5);
      var scale = Math.max(cw / iw, ch / ih);
      var renderedW = iw * scale;
      var renderedH = ih * scale;
      var offsetX = -(renderedW - cw) * opX;
      var offsetY = -(renderedH - ch) * opY;
      dots.forEach(function (dot) {
        var ix = parseFloat(dot.dataset.clDotX);
        var iy = parseFloat(dot.dataset.clDotY);
        if (isNaN(ix) || isNaN(iy)) return;
        var px = offsetX + (ix / 100) * renderedW;
        var py = offsetY + (iy / 100) * renderedH;
        dot.style.left = (px / cw * 100) + '%';
        dot.style.top = (py / ch * 100) + '%';
      });
    }

    function applyZoom(index) {
      if (!modalImage) return;
      if (REDUCED) { modalImage.style.transform = 'scale(1)'; return; }
      var dot = dots[index];
      if (!dot) { modalImage.style.transform = 'scale(1)'; return; }
      var x = parseFloat(dot.dataset.clDotX);
      var y = parseFloat(dot.dataset.clDotY);
      if (isNaN(x) || isNaN(y)) { modalImage.style.transform = 'scale(1)'; return; }
      var tx = (50 - x) * ZOOM;
      var ty = (50 - y) * ZOOM;
      modalImage.style.transform = 'translate(' + tx + '%, ' + ty + '%) scale(' + ZOOM + ')';
    }

    function syncExpanded(index) {
      for (var j = 0; j < dots.length; j++) {
        dots[j].setAttribute('aria-expanded', j === index ? 'true' : 'false');
      }
      for (var k = 0; k < pills.length; k++) {
        pills[k].setAttribute('aria-expanded', k === index ? 'true' : 'false');
      }
    }

    function showHotspot(index) {
      if (index < 0 || index >= total) return;

      for (var i = 0; i < contentStack.length; i++) {
        contentStack[i].hidden = (i !== index);
      }

      var visibleTitle = modal.querySelector('[data-cl-bodymap-content]:not([hidden]) .cl-bm-modal__title');
      if (visibleTitle && visibleTitle.id) modal.setAttribute('aria-labelledby', visibleTitle.id);

      syncExpanded(index);
      applyZoom(index);

      if (modalCounter) modalCounter.textContent = (index + 1) + ' / ' + total;
      currentIndex = index;
    }

    function openModal(index) {
      lastFocused = document.activeElement;
      showHotspot(index);

      if (typeof modal.showModal === 'function') {
        try { modal.showModal(); }
        catch (e) { modal.setAttribute('open', ''); }
      } else {
        modal.setAttribute('open', '');
      }

      // Defer one tick so the [open] paint settles before toggling is-open.
      // setTimeout(0) (not rAF) survives the top-layer hand-off in some Chromium
      // builds where an rAF queued right after showModal() gets starved.
      setTimeout(function () { modal.classList.add('is-open'); }, 0);
    }

    function closeModal() {
      modal.classList.remove('is-open');
      setTimeout(function () {
        if (typeof modal.close === 'function') {
          try { modal.close(); } catch (e) { modal.removeAttribute('open'); }
        } else {
          modal.removeAttribute('open');
        }
        syncExpanded(-1);
        if (modalImage) modalImage.style.transform = 'scale(1)';
        currentIndex = -1;
        if (lastFocused && typeof lastFocused.focus === 'function') {
          try { lastFocused.focus({ preventScroll: true }); } catch (e) {}
        }
      }, TRANSITION);
    }

    function cycle(delta) {
      if (currentIndex < 0) return;
      var next = (currentIndex + delta + total) % total;
      showHotspot(next);
    }

    // Dot + pill both open the same hotspot (Fatty15: tab click === dot click).
    dots.forEach(function (dot, i) {
      on(dot, 'click', function (e) { e.preventDefault(); openModal(i); });
    });
    pills.forEach(function (pill, i) {
      on(pill, 'click', function (e) { e.preventDefault(); openModal(i); });
    });

    if (modalClose) on(modalClose, 'click', closeModal);
    if (modalOverlay) on(modalOverlay, 'click', closeModal);
    if (modalPrev) on(modalPrev, 'click', function () { cycle(-1); });
    if (modalNext) on(modalNext, 'click', function () { cycle(1); });

    on(modal, 'cancel', function (e) { e.preventDefault(); closeModal(); });

    on(document, 'keydown', function (e) {
      if (!modal.classList.contains('is-open')) return;
      if (e.key === 'ArrowLeft') { e.preventDefault(); cycle(-1); }
      else if (e.key === 'ArrowRight') { e.preventDefault(); cycle(1); }
    });

    if (canvasImage) {
      if (canvasImage.complete && canvasImage.naturalWidth) {
        positionDots();
      } else {
        on(canvasImage, 'load', positionDots);
      }
    }
    var resizeRaf = 0;
    on(window, 'resize', function () {
      if (resizeRaf) cancelAnimationFrame(resizeRaf);
      resizeRaf = requestAnimationFrame(function () { resizeRaf = 0; positionDots(); });
    });

    on(document, 'shopify:section:unload', function (e) {
      if (!e.target || !e.target.contains || !e.target.contains(section)) return;
      teardown.forEach(function (fn) { try { fn(); } catch (err) {} });
      teardown.length = 0;
      section.dataset.clBodymapInit = '0';
    });
  }

  function initAll() {
    var sections = document.querySelectorAll('[data-cl-bodymap]');
    Array.prototype.forEach.call(sections, initSection);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initAll, { once: true });
  } else {
    initAll();
  }

  document.addEventListener('shopify:section:load', function (e) {
    var s = e.target && e.target.querySelector ? e.target.querySelector('[data-cl-bodymap]') : null;
    if (s) initSection(s);
  });
})();
